import React from 'react';

import { Menu } from '@base-ui/react/menu';
import { ListItemButton, ListItemText, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';

import { DeclarativeMenuItemIcon } from './DeclarativeMenuItemIcon';
import { ArrowRightIcon } from '../icons/ArrowRightIcon';
import { CheckMarkIcon } from '../icons/CheckMarkIcon';

export interface DeclarativeMenuItemProps {
  /**
   * The icon to be used for this menu item. If 'gap' is used, a space will be left equal to the
   * size of an icon. If 'none' is used, no gap will be applied; this should only be used for menus
   * where no menu item has an icon.
   */
  icon: React.ReactNode | 'gap' | 'none';
  isSubmenuTrigger: boolean;
  checked?: boolean;
  hint?: React.ReactNode;
  secondary?: React.ReactNode;
}

const useStyles = makeStyles()(() => ({
  menuItem: {
    '&[data-disabled]': {
      cursor: 'default',
      opacity: 0.38,
      pointerEvents: 'none',
    },
  },
}));

export function DeclarativeMenuItem(
  props: React.ComponentProps<typeof Menu.Item> & DeclarativeMenuItemProps
) {
  const { classes } = useStyles();
  const { checked, children, hint, isSubmenuTrigger, secondary, ...other } = props;
  let { icon } = props;
  const isCheckbox = typeof checked === 'boolean';

  const Component = isSubmenuTrigger
    ? Menu.SubmenuTrigger
    : isCheckbox
      ? Menu.CheckboxItem
      : Menu.Item;
  const hasOwnIcon = Boolean(icon) && icon !== 'gap' && icon !== 'none';

  if (isCheckbox && !hasOwnIcon) {
    if (isSubmenuTrigger) {
      /**
       * Base UI doesn't have a menu item component that combines SubmenuTrigger and CheckboxItem,
       * so menu items with submenus can't be CheckboxItems, and so can't use CheckboxItemIndicator.
       * Instead, we just show the checkmark icon.
       */
      icon = checked ? <CheckMarkIcon /> : null;
    } else {
      icon = <Menu.CheckboxItemIndicator render={<CheckMarkIcon />} />;
    }
  }

  return (
    <Component
      checked={checked}
      // CheckboxItem defaults closeOnClick to false; Menu.Item already defaults to true
      {...(isCheckbox && !isSubmenuTrigger ? { closeOnClick: true } : null)}
      className={classes.menuItem}
      render={<ListItemButton dense sx={{ gap: 1.5 }} />}
      {...other}
    >
      <DeclarativeMenuItemIcon icon={icon} />
      <ListItemText secondary={secondary}>{children}</ListItemText>
      {hint && (
        <Typography sx={{ color: 'text.secondary', flexShrink: 0, typography: 'body2' }}>
          {hint}
        </Typography>
      )}
      {isCheckbox && hasOwnIcon && !isSubmenuTrigger && (
        <Menu.CheckboxItemIndicator render={<CheckMarkIcon />} />
      )}
      {isSubmenuTrigger && <ArrowRightIcon />}
    </Component>
  );
}
